
import { useState } from 'react';

interface PaidInOutModalProps {
  type: 'in' | 'out';
  onClose: () => void;
  onConfirm: (amount: number, reason: string, note: string) => void;
  userRole: 'cashier' | 'supervisor' | 'manager';
  currentBalance: number;
}

interface AuditLogEntry {
  id: string;
  type: 'paid_in' | 'paid_out';
  amount: number;
  reason: string;
  note: string;
  timestamp: string;
  user: string;
  userRole: string;
  terminal: string;
  store: string;
  previousBalance: number;
  newBalance: number;
}

export default function PaidInOutModal({ type, onClose, onConfirm, userRole, currentBalance }: PaidInOutModalProps) {
  const [amount, setAmount] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Paid In reasons based on business requirements
  const paidInReasons = [
    {
      id: 'starting_float',
      label: 'Adding Starting Float',
      description: 'Add base cash at the beginning of shift for giving change',
      icon: 'ri-money-dollar-circle-line',
    },
    {
      id: 'replacing_cash',
      label: 'Replacing Removed Cash',
      description: 'Add cash back after a previous cash drop',
      icon: 'ri-refresh-line',
    },
    {
      id: 'petty_cash_return',
      label: 'Petty Cash Return',
      description: 'Return of petty cash previously taken out',
      icon: 'ri-wallet-3-line',
    },
    {
      id: 'cash_reimbursement',
      label: 'Cash Reimbursement',
      description: 'Reimbursement for expenses or corrections',
      icon: 'ri-hand-coin-line',
    },
    {
      id: 'other',
      label: 'Other',
      description: 'Other non-sales cash entry',
      icon: 'ri-more-line',
    },
  ];

  // Paid Out reasons based on business requirements
  const paidOutReasons = [
    {
      id: 'office_supplies',
      label: 'Office Supplies',
      description: 'Cash taken to pay for office supplies',
      icon: 'ri-pencil-ruler-2-line',
    },
    {
      id: 'cleaning_services',
      label: 'Cleaning Services',
      description: 'Payment for cleaning services',
      icon: 'ri-brush-line',
    },
    {
      id: 'delivery_tips',
      label: 'Delivery Tips',
      description: 'Tips for delivery personnel',
      icon: 'ri-e-bike-line',
    },
    {
      id: 'cash_drop',
      label: 'Cash Drop for Safety',
      description: 'Remove excess cash and place in safe to reduce risk',
      icon: 'ri-safe-2-line',
    },
    {
      id: 'transfer_money',
      label: 'Transfer to Another Drawer',
      description: 'Move cash from this drawer to another register',
      icon: 'ri-exchange-funds-line',
    },
    {
      id: 'petty_cash',
      label: 'Other Small Expenses',
      description: 'Other small cash expenses not listed above',
      icon: 'ri-money-dollar-box-line',
    },
  ];

  const reasons = type === 'in' ? paidInReasons : paidOutReasons;

  const generateAuditId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${type === 'in' ? 'PIN' : 'POUT'}-${timestamp}-${random}`;
  };

  const handleConfirm = () => {
    const numAmount = parseFloat(amount);

    if (!amount || numAmount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }

    if (userRole === 'cashier') {
      setError('Supervisor permission required for Paid In/Out operations');
      return;
    }

    if (type === 'out' && numAmount > currentBalance) {
      setError('Amount exceeds current drawer balance');
      return;
    }

    setIsProcessing(true);

    // Create audit log entry
    const auditEntry: AuditLogEntry = {
      id: generateAuditId(),
      type: type === 'in' ? 'paid_in' : 'paid_out',
      amount: numAmount,
      reason: reasons.find(r => r.id === selectedReason)?.label || selectedReason,
      note: note.trim(),
      timestamp: new Date().toISOString(),
      user: 'Sarah Johnson', // Would come from auth context
      userRole: userRole,
      terminal: 'Lane 3',
      store: 'Downtown Store',
      previousBalance: currentBalance,
      newBalance: type === 'in' ? currentBalance + numAmount : currentBalance - numAmount,
    };

    // Store audit log
    const existingAuditLogs = JSON.parse(sessionStorage.getItem('paidInOutAuditLog') || '[]');
    existingAuditLogs.push(auditEntry);
    sessionStorage.setItem('paidInOutAuditLog', JSON.stringify(existingAuditLogs));

    // Simulate processing delay
    setTimeout(() => {
      setIsProcessing(false);
      setShowSuccess(true);

      // Auto close after showing success
      setTimeout(() => {
        onConfirm(numAmount, auditEntry.reason, note);
      }, 1500);
    }, 800);
  };

  const newBalance = type === 'in' 
    ? currentBalance + (parseFloat(amount) || 0)
    : currentBalance - (parseFloat(amount) || 0);

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-8 text-center">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${type === 'in' ? 'bg-green-100' : 'bg-red-100'} mb-4`}>
            <i className={`ri-check-line text-3xl ${type === 'in' ? 'text-green-600' : 'text-red-600'}`}></i>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {type === 'in' ? 'Paid In Successful' : 'Paid Out Successful'}
          </h3>
          <p className="text-gray-600 mb-4">
            ${parseFloat(amount).toFixed(2)} has been {type === 'in' ? 'added to' : 'removed from'} the drawer
          </p>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">New Drawer Balance:</span>
              <span className="text-lg font-bold text-gray-900">${newBalance.toFixed(2)}</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            <i className="ri-file-list-3-line mr-1"></i>
            Transaction logged in audit trail
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className={`flex items-center justify-between p-4 border-b ${type === 'in' ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${type === 'in' ? 'bg-green-100' : 'bg-red-100'}`}>
              <i className={`${type === 'in' ? 'ri-arrow-down-circle-line text-green-600' : 'ri-arrow-up-circle-line text-red-600'} text-xl`}></i>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {type === 'in' ? 'Paid In' : 'Paid Out'}
              </h2>
              <p className="text-xs text-gray-500">
                {type === 'in' ? 'Add cash to drawer (non-sales)' : 'Remove cash from drawer'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Info Banner */}
          <div className={`rounded-lg p-4 ${type === 'in' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-start gap-3">
              <i className={`ri-information-line text-lg mt-0.5 ${type === 'in' ? 'text-green-600' : 'text-red-600'}`}></i>
              <div>
                <p className={`text-sm font-medium ${type === 'in' ? 'text-green-800' : 'text-red-800'}`}>
                  {type === 'in' ? 'What is Paid In?' : 'What is Paid Out?'}
                </p>
                <p className={`text-xs mt-1 ${type === 'in' ? 'text-green-700' : 'text-red-700'}`}>
                  {type === 'in' 
                    ? 'Use this to add cash to the drawer for non-sales purposes such as starting float, replacing removed cash, or recording petty cash returns.'
                    : 'Use this to remove cash from the drawer for purposes such as bank deposits, petty cash expenses, or cash drops.'}
                </p>
              </div>
            </div>
          </div>

          {userRole === 'cashier' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-amber-500 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-amber-800">Supervisor Permission Required</p>
                <p className="text-xs text-amber-600 mt-1">Cashiers cannot perform Paid In/Out operations. Please call a supervisor.</p>
              </div>
            </div>
          )}

          {/* Current Balance Display */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Current Drawer Balance:</span>
              <span className="text-lg font-semibold text-gray-900">${currentBalance.toFixed(2)}</span>
            </div>
            {amount && parseFloat(amount) > 0 && (
              <>
                <div className="flex justify-between items-center py-2 border-t border-gray-300">
                  <span className="text-sm text-gray-600">{type === 'in' ? 'Amount to Add:' : 'Amount to Remove:'}</span>
                  <span className={`text-sm font-medium ${type === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                    {type === 'in' ? '+' : '-'}${parseFloat(amount).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                  <span className="text-sm font-medium text-gray-700">New Balance:</span>
                  <span className={`text-lg font-bold ${newBalance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ${newBalance.toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setError('');
                }}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-xl font-semibold"
              />
            </div>
          </div>

          {/* Reason Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {reasons.map((reason) => (
                <label
                  key={reason.id}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedReason === reason.id 
                      ? type === 'in' 
                        ? 'border-green-500 bg-green-50 ring-1 ring-green-500' 
                        : 'border-red-500 bg-red-50 ring-1 ring-red-500'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason.id}
                    checked={selectedReason === reason.id}
                    onChange={(e) => {
                      setSelectedReason(e.target.value);
                      setError('');
                    }}
                    className="w-4 h-4 mt-0.5 text-teal-600 cursor-pointer"
                  />
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    selectedReason === reason.id 
                      ? type === 'in' ? 'bg-green-100' : 'bg-red-100'
                      : 'bg-gray-100'
                  }`}>
                    <i className={`${reason.icon} ${
                      selectedReason === reason.id 
                        ? type === 'in' ? 'text-green-600' : 'text-red-600'
                        : 'text-gray-500'
                    }`}></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-800">{reason.label}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{reason.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Note Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter additional details about this transaction..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
              rows={3}
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{note.length}/200 characters</p>
          </div>

          {/* Audit Trail Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <i className="ri-shield-check-line text-blue-500 text-lg mt-0.5"></i>
            <div>
              <p className="text-sm font-medium text-blue-800">Audit Trail</p>
              <p className="text-xs text-blue-600 mt-1">
                This transaction will be logged with your name, time, terminal, and reason for accountability and fraud prevention.
              </p>
            </div>
          </div>

          {/* What Happens Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">What happens when confirmed:</p>
            <ul className="space-y-1.5">
              <li className="flex items-center gap-2 text-xs text-gray-600">
                <i className={`ri-checkbox-circle-line ${type === 'in' ? 'text-green-500' : 'text-red-500'}`}></i>
                Drawer balance will {type === 'in' ? 'increase' : 'decrease'}
              </li>
              <li className="flex items-center gap-2 text-xs text-gray-600">
                <i className={`ri-checkbox-circle-line ${type === 'in' ? 'text-green-500' : 'text-red-500'}`}></i>
                Non-sales transaction record created
              </li>
              <li className="flex items-center gap-2 text-xs text-gray-600">
                <i className={`ri-checkbox-circle-line ${type === 'in' ? 'text-green-500' : 'text-red-500'}`}></i>
                No invoice or product linked
              </li>
              <li className="flex items-center gap-2 text-xs text-gray-600">
                <i className={`ri-checkbox-circle-line ${type === 'in' ? 'text-green-500' : 'text-red-500'}`}></i>
                Logged in audit trail with full details
              </li>
            </ul>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={userRole === 'cashier' || isProcessing}
            className={`flex-1 px-4 py-2.5 ${type === 'in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2`}
          >
            {isProcessing ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Processing...
              </>
            ) : (
              <>
                <i className={type === 'in' ? 'ri-arrow-down-circle-line' : 'ri-arrow-up-circle-line'}></i>
                Confirm {type === 'in' ? 'Paid In' : 'Paid Out'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
